// src/pages/admin/AdminAssinatura.js — Painel de Assinatura
import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Stack, Chip,
  Divider, Alert, CircularProgress, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiCreditCard, FiFileText, FiCalendar, FiExternalLink,
  FiAlertCircle, FiCheckCircle, FiClock, FiXCircle, FiRefreshCw
} from 'react-icons/fi';
import AppSidebar from '../../components/AppSidebar';
import EmbeddedSignupButton from '../../components/EmbeddedSignupButton';
import JanelaAtivar from '../../components/assinatura/JanelaAtivar';
import api from '../../services/Api';

// ── Configurações de status ───────────────────────────────────────────────────
const STATUS_CONFIG = {
  ACTIVE:   { label: 'Ativa',           color: '#16a34a', bg: '#f0fdf4',  icon: FiCheckCircle },
  TRIALING: { label: 'Em Teste',        color: '#0ea5e9', bg: '#f0f9ff',  icon: FiClock },
  PAST_DUE: { label: 'Pagamento Atrasado', color: '#d97706', bg: '#fffbeb', icon: FiAlertCircle },
  CANCELED: { label: 'Cancelada',       color: '#dc2626', bg: '#fef2f2',  icon: FiAlertCircle },
  INACTIVE: { label: 'Inativa',         color: '#64748b', bg: '#f8fafc',  icon: FiAlertCircle },
};

// Formata data ISO para pt-BR
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
};

// Formata tipo de transação do ledger
const fmtReason = (reason) => {
  const map = {
    PLAN_RENEWAL:    'Renovação do Plano',
    ADDON_PURCHASE:  'Compra de Pacote',
    TEMPLATE_SENT:   'Template Enviado',
    ADMIN_ADJUSTMENT:'Ajuste Administrativo',
  };
  return map[reason] || reason;
};

export default function AdminAssinatura() {
  const navigate = useNavigate();
  const token    = localStorage.getItem('token');
  const user     = JSON.parse(localStorage.getItem('user') || '{}');

  const [status, setStatus]         = useState(null);
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [loadingReactivate, setLoadingReactivate] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [ativarAberta, setAtivarAberta] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  // ── Carrega todos os dados de uma vez ─────────────────────────────────────
  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const fetchAll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [resStatus, resHistory] = await Promise.all([
          api.get('/billing/subscription-status', { headers }),
          api.get('/billing/template-history',    { headers }),
        ]);
        setStatus(resStatus.data);
        setHistory(resHistory.data);
      } catch (err) {
        setMsg({ type: 'error', text: 'Erro ao carregar dados da assinatura.' });
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token, navigate]);


  // ── Abrir portal da Stripe ────────────────────────────────────────────────
  const handlePortal = async () => {
    setLoadingPortal(true);
    try {
      const res = await api.post(
        '/billing/customer-portal',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(res.data.url, '_blank');
    } catch (err) {
      setMsg({ type: 'error', text: 'Erro ao abrir portal de assinatura.' });
    } finally {
      setLoadingPortal(false);
    }
  };

  // ── Cancelar / Reativar ───────────────────────────────────────────────────
  // Endpoints próprios (set/26). Antes o cliente ia no Portal do Stripe e a
  // config de "Cancellations" da conta decidia o comportamento — deu ruim
  // durante o trial (cobrou o cliente). Agora tudo passa por aqui:
  //  - cancelar = cancel_at_period_end=true (sem cobrança, mantém acesso)
  //  - reativar = cancel_at_period_end=false (idempotente)
  const handleCancel = async () => {
    setLoadingCancel(true);
    try {
      const res = await api.post('/billing/cancel-subscription', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(s => ({
        ...s,
        cancel_at_period_end: true,
        current_period_end: res.data.current_period_end || s.current_period_end,
      }));
      setConfirmCancelOpen(false);
      setMsg({
        type: 'success',
        text: 'Cancelamento agendado. Você mantém acesso até o fim do período atual.',
      });
    } catch (err) {
      setMsg({ type: 'error', text: 'Erro ao cancelar assinatura.' });
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleReactivate = async () => {
    setLoadingReactivate(true);
    try {
      await api.post('/billing/reactivate-subscription', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(s => ({ ...s, cancel_at_period_end: false }));
      setMsg({ type: 'success', text: 'Assinatura reativada.' });
    } catch (err) {
      setMsg({ type: 'error', text: 'Erro ao reativar assinatura.' });
    } finally {
      setLoadingReactivate(false);
    }
  };

  const planoAtual    = status?.plan_type || 'STARTER';
  const statusCfg     = STATUS_CONFIG[status?.subscription_status] || STATUS_CONFIG.INACTIVE;
  const StatusIcon    = statusCfg.icon;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppSidebar user={user} />

      <Box sx={{ flexGrow: 1, ml: { xs: 0, md: 'var(--sidebar-width, 260px)' }, transition: 'margin-left 0.25s ease', p: { xs: 2, md: 4 }, maxWidth: 1000 }}>

        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <Button
            startIcon={<FiArrowLeft />}
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
          >
            Voltar
          </Button>
          <Divider orientation="vertical" flexItem />
          <FiCreditCard size={22} color="#3b82f6" />
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              Minha Assinatura
            </Typography>
            <Typography variant="body2" color="#64748b">
              Gerencie seu plano, templates e histórico de uso
            </Typography>
          </Box>
        </Stack>

        {msg.text && (
          <Alert severity={msg.type || 'info'} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#25d366' }} />
            <Typography variant="body2" color="#94a3b8" mt={2}>Carregando dados da assinatura...</Typography>
          </Box>
        ) : (
          <Stack spacing={3}>

            {/* ── Bloco 1: Status da Assinatura ─────────────────────────── */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                  Status da Assinatura
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Stack spacing={2}>
                    {/* Status */}
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Chip
                        icon={<StatusIcon size={14} color={statusCfg.color} />}
                        label={statusCfg.label}
                        size="small"
                        sx={{ bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 700, fontSize: '0.8rem', height: 28 }}
                      />
                      <Chip
                        label={`Plano ${planoAtual}`}
                        size="small"
                        sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: '0.8rem', height: 28 }}
                      />
                    </Stack>

                    {/* Próxima cobrança / fim do acesso */}
                    {status?.current_period_end && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FiCalendar size={14} color="#94a3b8" />
                        <Typography variant="body2" color="#64748b">
                          {status.cancel_at_period_end
                            ? <>Acesso encerra em <strong>{fmtDate(status.current_period_end)}</strong></>
                            : <>Próxima cobrança: <strong>{fmtDate(status.current_period_end)}</strong></>}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {/* Ações — variam por estado da assinatura */}
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    {/* Out/26: no teste, ativar já (encerra o teste e cobra
                        na hora) — a mesma janela do selo da TopBar. */}
                    {status?.subscription_status === 'TRIALING' && user?.role === 'SUPERVISOR' && (
                      <Button
                        variant="contained"
                        startIcon={<FiCheckCircle size={14} />}
                        onClick={() => setAtivarAberta(true)}
                        sx={{
                          bgcolor: '#00B865', color: '#fff',
                          textTransform: 'none', fontWeight: 700, borderRadius: 2,
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#009E57', boxShadow: 'none' }
                        }}
                      >
                        Ativar assinatura agora
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={loadingPortal ? <CircularProgress size={14} /> : <FiExternalLink size={14} />}
                      onClick={handlePortal}
                      disabled={loadingPortal}
                      sx={{
                        borderColor: '#e2e8f0', color: '#334155',
                        textTransform: 'none', fontWeight: 600, borderRadius: 2,
                        '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', bgcolor: 'rgba(59,130,246,0.04)' }
                      }}
                    >
                      Gerenciar cartão
                    </Button>

                    {status?.cancel_at_period_end ? (
                      <Button
                        variant="contained"
                        startIcon={loadingReactivate ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <FiRefreshCw size={14} />}
                        onClick={handleReactivate}
                        disabled={loadingReactivate}
                        sx={{
                          bgcolor: '#16a34a', color: '#fff',
                          textTransform: 'none', fontWeight: 700, borderRadius: 2,
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#15803d', boxShadow: 'none' }
                        }}
                      >
                        Reativar assinatura
                      </Button>
                    ) : (
                      ['ACTIVE', 'TRIALING'].includes(status?.subscription_status) && (
                        <Button
                          variant="text"
                          startIcon={<FiXCircle size={14} />}
                          onClick={() => setConfirmCancelOpen(true)}
                          sx={{
                            color: '#94a3b8',
                            textTransform: 'none', fontWeight: 600, borderRadius: 2,
                            '&:hover': { color: '#dc2626', bgcolor: 'rgba(220,38,38,0.04)' }
                          }}
                        >
                          Cancelar assinatura
                        </Button>
                      )
                    )}
                  </Stack>
                </Stack>

                {/* Aviso quando o cancelamento está agendado */}
                {status?.cancel_at_period_end && (
                  <Alert
                    severity="warning"
                    icon={<FiAlertCircle />}
                    sx={{ mt: 2.5, borderRadius: 2, alignItems: 'center' }}
                  >
                    Cancelamento agendado. Sua conta continua ativa até{' '}
                    <strong>{fmtDate(status.current_period_end)}</strong> — sem cobrança adicional.
                    Você pode reativar a qualquer momento antes dessa data.
                  </Alert>
                )}
              </Box>
            </Paper>

            {/* ── Bloco 2: Templates ───────────────────────────────────── */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      Templates
                    </Typography>
                    <Typography variant="caption" color="#94a3b8" sx={{ fontSize: '0.72rem' }}>
                      Cobrados pela Meta
                    </Typography>
                  </Box>
                  <Button
                    component="a"
                    href="https://business.facebook.com/wa/manage"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    endIcon={<FiExternalLink size={13} />}
                    sx={{
                      borderColor: '#3b82f6', color: '#3b82f6',
                      textTransform: 'none', fontWeight: 700,
                      borderRadius: 2, fontSize: '0.8rem',
                      '&:hover': { borderColor: '#2563eb', color: '#2563eb', bgcolor: 'rgba(59,130,246,0.04)' }
                    }}
                  >
                    Ver na Meta
                  </Button>
                </Stack>
              </Box>
              {/* A Cetto é Tech Provider da Meta, não Solution Partner: não
                  pode intermediar o faturamento de templates. Quem tarifa e
                  cobra é a Meta, no meio de pagamento que o cliente cadastrou
                  no Embedded Signup. Este bloco é puramente informativo — a
                  Cetto não tem o dado de consumo nem o produto pra vender. */}
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="#475569" sx={{ lineHeight: 1.7 }}>
                  Os templates enviados pela sua operação são tarifados e cobrados <b>diretamente pela Meta</b>, no
                  cartão que você cadastrou durante a ativação da conta. A Cetto cobra apenas a mensalidade da
                  plataforma — não revende, não credita e não fatura templates.
                </Typography>
                <Typography variant="body2" color="#94a3b8" sx={{ mt: 1.5, fontSize: '0.82rem', lineHeight: 1.7 }}>
                  Para consultar consumo, tarifas e faturas de templates, use o botão <b>Ver na Meta</b> acima —
                  é lá que também se troca o cartão cobrado. O limite diário de disparo por chip é do seu plano e
                  aparece no dashboard.
                </Typography>

                {/* Embedded Signup — adicionar/reconectar número. O componente
                    é desenhado pro tema escuro (sub-label e estados de erro em
                    texto claro), então vive num painel escuro pra continuar
                    legível nesta tela clara, em vez de duplicar a estilização. */}
                <Box sx={{ mt: 3, p: 2.5, borderRadius: 2, bgcolor: '#0f172a' }}>
                  <Typography sx={{ color: '#e2e8f0', fontSize: '0.88rem', fontWeight: 700, mb: 0.5 }}>
                    Adicionar ou reconectar um número
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', lineHeight: 1.6, mb: 2 }}>
                    {/* set/26: era "conectar um NOVO número", que passou a
                        contradizer o aviso de Coexistência logo abaixo do
                        botão (o número precisa já existir no app). */}
                    Abre o fluxo oficial da Meta para ligar um número do seu WhatsApp Business à Cetto
                    ou refazer uma ativação que ficou incompleta.
                  </Typography>
                  <EmbeddedSignupButton
                    onSuccess={() => setTimeout(() => window.location.reload(), 1500)}
                    onError={(err) => console.error('Embedded Signup error:', err)}
                    deviceName={user?.company_name || undefined}
                  />
                </Box>
              </Box>
            </Paper>

            {/* ── Bloco 3: Histórico de Transações ─────────────────────── */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                  Histórico de Templates
                </Typography>
              </Box>

              {history.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <FiFileText size={36} color="#cbd5e1" />
                  <Typography variant="body2" color="#94a3b8" mt={1.5}>
                    Nenhuma transação encontrada ainda.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        {['Data', 'Tipo', 'Motivo', 'Quantidade'].map(col => (
                          <TableCell key={col} sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.78rem', py: 1.5 }}>
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((entry, i) => (
                        <TableRow
                          key={entry.id || i}
                          sx={{ '&:hover': { bgcolor: '#f8fafc' }, '&:last-child td': { border: 0 } }}
                        >
                          <TableCell sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                            {fmtDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.type === 'CREDIT' ? 'Crédito' : 'Débito'}
                              size="small"
                              sx={{
                                bgcolor: entry.type === 'CREDIT' ? '#f0fdf4' : '#fef2f2',
                                color:   entry.type === 'CREDIT' ? '#16a34a' : '#dc2626',
                                fontWeight: 700, fontSize: '0.72rem', height: 22, borderRadius: '6px'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#334155', fontSize: '0.82rem' }}>
                            {fmtReason(entry.reason)}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={entry.type === 'CREDIT' ? '#16a34a' : '#dc2626'}
                            >
                              {entry.type === 'CREDIT' ? '+' : '-'}{entry.amount}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

          </Stack>
        )}
      </Box>

      <JanelaAtivar
        aberta={ativarAberta}
        onFechar={() => setAtivarAberta(false)}
        onAtivada={async () => {
          try {
            const r = await api.get('/billing/subscription-status');
            setStatus(r.data);
          } catch { /* a página recarrega o resto na próxima visita */ }
          setMsg({ type: 'success', text: 'Assinatura ativada. Agenda, Automação e Formulários estão liberados.' });
        }}
      />

      {/* Confirmação de cancelamento — só chama a API depois do OK explícito */}
      <Dialog
        open={confirmCancelOpen}
        onClose={() => !loadingCancel && setConfirmCancelOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pb: 1 }}>
          Cancelar assinatura?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6 }}>
            Você continua com acesso até{' '}
            <strong>{fmtDate(status?.current_period_end)}</strong> e{' '}
            <strong>nenhuma cobrança adicional será feita</strong>. Depois dessa
            data a conta é encerrada.
            <br /><br />
            Pode reativar quando quiser antes disso, sem custo extra.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmCancelOpen(false)}
            disabled={loadingCancel}
            sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
          >
            Voltar
          </Button>
          <Button
            onClick={handleCancel}
            disabled={loadingCancel}
            variant="contained"
            startIcon={loadingCancel ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
            sx={{
              bgcolor: '#dc2626', color: '#fff',
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' }
            }}
          >
            Cancelar assinatura
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
